import {
  changePeriodStatusSchema,
  createFiscalYearSchema,
  type FiscalPeriod,
  type FiscalPeriodStatus,
  type FiscalYear,
  YEAR_END_STEPS,
  type YearEndResult,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  cn,
  Dialog,
  EmptyState,
  FormField,
  Input,
  SkeletonRows,
  StatusBadge,
  Textarea,
  type Tone,
  useToast,
} from "@station/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CalendarRange, Check, Plus } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AccountingPage, companySearchShape } from "../../../features/accounting/company.js";
import { PERIOD_STATUS_TONE } from "../../../features/accounting/shared.js";
import { zodErrorMap } from "../../../features/accounting/validation.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";
import { queryKeys } from "../../../lib/query.js";
import { usePermissions } from "../../../lib/session.js";
import { useStepUp } from "../../../lib/step-up.js";

export const Route = createFileRoute("/_app/muhasebe/donemler")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: PeriodsPage,
});

const STATUS_RANK: Record<FiscalPeriodStatus, number> = { OPEN: 0, SOFT_CLOSED: 1, CLOSED: 2 };
const CHECK_TONE: Record<"OK" | "WARNING" | "BLOCKER" | "NOT_APPLICABLE", Tone> = {
  OK: "success",
  WARNING: "warning",
  BLOCKER: "danger",
  NOT_APPLICABLE: "neutral",
};

function PeriodsPage() {
  const { t } = useTranslation();
  const canManageChart = usePermissions().has("accounting.chart.manage");
  const [addingYear, setAddingYear] = useState(false);
  return (
    <AccountingPage
      title={t("menu.fiscalPeriods")}
      description={t("accounting.periods.description")}
      actions={
        canManageChart ? (
          <Button onClick={() => setAddingYear(true)}>
            <Plus aria-hidden="true" />
            {t("accounting.periods.newYear")}
          </Button>
        ) : null
      }
    >
      {({ companyId }) => (
        <>
          <FiscalYears companyId={companyId} />
          <NewYearDialog companyId={companyId} open={addingYear} onOpenChange={setAddingYear} />
        </>
      )}
    </AccountingPage>
  );
}

function FiscalYears({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const years = useQuery({
    queryKey: queryKeys.accounting(companyId, "fiscal-years"),
    queryFn: async () =>
      (await api.GET("/accounting/fiscal-years", { params: { query: { companyId } } })).data!.items,
  });
  const [change, setChange] = useState<{
    period: FiscalPeriod;
    year: number;
    target: FiscalPeriodStatus;
  } | null>(null);
  const [wizardYear, setWizardYear] = useState<FiscalYear | null>(null);

  if (years.error) return <Alert tone="danger">{errorMessage(years.error)}</Alert>;
  if (!years.data) return <SkeletonRows rows={8} />;
  if (years.data.length === 0)
    return (
      <EmptyState
        icon={<CalendarRange />}
        title={t("accounting.periods.empty")}
        description={t("accounting.periods.emptyHint")}
      />
    );
  const canClose = permissions.has("accounting.period.close");
  const sorted = [...years.data].sort((left, right) => right.year - left.year);

  return (
    <div className="grid gap-6">
      {sorted.map((year) => (
        <Card key={year.id} className="overflow-x-auto">
          <header className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">
                {t("accounting.periods.yearTitle", { year: year.year })}
              </h2>
              <StatusBadge tone={year.status === "OPEN" ? "success" : "neutral"}>
                {t(`enums.fiscalYearStatus.${year.status}`)}
              </StatusBadge>
            </div>
            {permissions.has("accounting.entry.manage") ? (
              <Button variant="outline" size="sm" onClick={() => setWizardYear(year)}>
                {t("accounting.yearEnd.action")}
              </Button>
            ) : null}
          </header>
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">{t("accounting.periods.caption", { year: year.year })}</caption>
            <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th scope="col" className="px-3 py-2 text-left font-semibold">
                  {t("accounting.periods.period")}
                </th>
                <th scope="col" className="px-3 py-2 text-left font-semibold">
                  {t("accounting.periods.range")}
                </th>
                <th scope="col" className="px-3 py-2 text-left font-semibold">
                  {t("common.status")}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-semibold">
                  {t("accounting.periods.drafts")}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-semibold">
                  {t("accounting.periods.posted")}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-semibold">
                  <span className="sr-only">{t("common.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {year.periods.map((period) => {
                const label = t("accounting.periods.periodLabel", { year: year.year, no: period.periodNo });
                const targets = (["SOFT_CLOSED", "CLOSED", "OPEN"] as const).filter(
                  (target) =>
                    target !== period.status &&
                    // Yeniden açma tek adımdır: kapalı/geçici kapalı dönem doğrudan AÇIK duruma alınır.
                    (STATUS_RANK[target] > STATUS_RANK[period.status] || target === "OPEN"),
                );
                return (
                  <tr key={period.id} className="border-t">
                    <th scope="row" className="px-3 py-2 text-left font-medium tabular">
                      {String(period.periodNo).padStart(2, "0")}
                    </th>
                    <td className="whitespace-nowrap px-3 py-2 tabular">
                      {formatDate(period.startDate)} – {formatDate(period.endDate)}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge tone={PERIOD_STATUS_TONE[period.status]}>
                        {t(`enums.fiscalPeriodStatus.${period.status}`)}
                      </StatusBadge>
                      {period.statusReason ? (
                        <p className="mt-1 max-w-xs text-xs text-muted-foreground">{period.statusReason}</p>
                      ) : null}
                    </td>
                    <td
                      className={cn("px-3 py-2 text-right tabular", period.draftCount > 0 && "font-semibold")}
                    >
                      {period.draftCount}
                    </td>
                    <td className="px-3 py-2 text-right tabular">{period.postedCount}</td>
                    <td className="whitespace-nowrap px-3 py-1 text-right">
                      {canClose
                        ? targets.map((target) => (
                            <Button
                              key={target}
                              variant="ghost"
                              size="sm"
                              aria-label={`${label}: ${t(`accounting.periods.actions.${target}`)}`}
                              onClick={() => setChange({ period, year: year.year, target })}
                            >
                              {t(`accounting.periods.actions.${target}`)}
                            </Button>
                          ))
                        : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      ))}

      {change ? (
        <StatusChangeDialog
          key={`${change.period.id}-${change.target}`}
          companyId={companyId}
          change={change}
          onClose={() => setChange(null)}
        />
      ) : null}
      {wizardYear ? (
        <YearEndWizard
          key={wizardYear.id}
          companyId={companyId}
          year={wizardYear}
          onClose={() => setWizardYear(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * Dönem durumu değişikliği. Kapatmadan önce KONTROL LİSTESİ gösterilir; BLOCKER varken "kapat" kapalıdır.
 * Yeniden açmada gerekçe zorunludur. İşlem step-up ister: gerekirse kimlik doğrulama penceresi açılır ve yinelenir.
 */
function StatusChangeDialog({
  companyId,
  change,
  onClose,
}: {
  companyId: string;
  change: { period: FiscalPeriod; year: number; target: FiscalPeriodStatus };
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const withStepUp = useStepUp();
  const { period, year, target } = change;
  const reopening = STATUS_RANK[target] < STATUS_RANK[period.status];
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const checklist = useQuery({
    queryKey: queryKeys.accounting(companyId, "checklist", period.id),
    enabled: !reopening,
    staleTime: 0,
    queryFn: async () =>
      (await api.GET("/accounting/fiscal-periods/{id}/checklist", { params: { path: { id: period.id } } }))
        .data!,
  });

  const apply = useMutation({
    mutationFn: async () => {
      const body = changePeriodStatusSchema.parse({
        status: target,
        reason: reason.trim() || null,
        version: period.version,
      });
      return withStepUp(() =>
        api.POST("/accounting/fiscal-periods/{id}/status", { params: { path: { id: period.id } }, body }),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.accounting(companyId) });
      toast({ tone: "success", title: t(`accounting.periods.done.${target}`) });
      onClose();
    },
    onError: (error) => setFormError(errorMessage(error)),
  });

  const blocked = !reopening && target === "CLOSED" && checklist.data?.canClose === false;
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t(`accounting.periods.dialogTitle.${target}`, { year, no: period.periodNo })}
      description={
        reopening ? t("accounting.periods.reopenDescription") : t("accounting.periods.checklistDescription")
      }
      className="max-w-2xl"
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setFormError(null);
          if (reopening) {
            const parsed = changePeriodStatusSchema.shape.reason.safeParse(reason.trim() || null, {
              error: zodErrorMap,
            });
            if (reason.trim() === "") return setReasonError(t("common.required"));
            if (!parsed.success) return setReasonError(parsed.error.issues[0]!.message);
          }
          setReasonError(null);
          apply.mutate();
        }}
      >
        {formError ? <Alert tone="danger">{formError}</Alert> : null}
        {reopening ? (
          <FormField
            label={t("accounting.periods.reason")}
            description={t("accounting.periods.reasonHint")}
            error={reasonError ?? undefined}
            required
          >
            {(control) => (
              <Textarea
                {...control}
                rows={3}
                maxLength={500}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                autoFocus
              />
            )}
          </FormField>
        ) : checklist.error ? (
          <Alert tone="danger">{errorMessage(checklist.error)}</Alert>
        ) : !checklist.data ? (
          <SkeletonRows rows={4} />
        ) : (
          <>
            <ul className="grid gap-2" aria-label={t("accounting.periods.checklist")}>
              {checklist.data.items.map((item) => (
                <li key={item.key} className="flex items-start justify-between gap-3 rounded-md border p-3">
                  <div className="grid gap-0.5">
                    <p className="text-sm font-medium">{t(`accounting.periods.checks.${item.key}`)}</p>
                    <p className="text-sm text-muted-foreground">{item.detail}</p>
                  </div>
                  <StatusBadge tone={CHECK_TONE[item.status]} className="shrink-0">
                    {t(`enums.checkStatus.${item.status}`)}
                  </StatusBadge>
                </li>
              ))}
            </ul>
            {blocked ? <Alert tone="danger">{t("accounting.periods.blocked")}</Alert> : null}
            {target === "SOFT_CLOSED" ? (
              <Alert tone="info">{t("accounting.periods.softCloseNote")}</Alert>
            ) : null}
          </>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            variant={reopening ? "destructive" : "primary"}
            loading={apply.isPending}
            disabled={blocked || (!reopening && !checklist.data)}
          >
            {t(`accounting.periods.actions.${target}`)}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function NewYearDialog({
  companyId,
  open,
  onOpenChange,
}: {
  companyId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [yearText, setYearText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: async (year: number) => api.POST("/accounting/fiscal-years", { body: { companyId, year } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.accounting(companyId, "fiscal-years") });
      toast({ tone: "success", title: t("accounting.periods.yearCreated") });
      setYearText("");
      onOpenChange(false);
    },
    onError: (caught) => setError(errorMessage(caught)),
  });
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("accounting.periods.newYear")}
      description={t("accounting.periods.newYearDescription")}
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          // Yıl bir TAKVİM yılıdır (tutar değil); tam sayı olarak doğrulanır.
          const parsed = createFiscalYearSchema.safeParse(
            { companyId, year: /^\d{4}$/.test(yearText.trim()) ? Number(yearText.trim()) : Number.NaN },
            { error: zodErrorMap },
          );
          if (!parsed.success) return setError(t("accounting.periods.yearInvalid"));
          setError(null);
          create.mutate(parsed.data.year);
        }}
      >
        <FormField label={t("accounting.periods.year")} error={error ?? undefined} required>
          {(control) => (
            <Input
              {...control}
              inputMode="numeric"
              maxLength={4}
              className="w-32 tabular"
              value={yearText}
              onChange={(event) => setYearText(event.target.value)}
              autoFocus
            />
          )}
        </FormField>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={create.isPending}>
            {t("accounting.periods.createYear")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Yıl sonu sihirbazı: üç adım SIRAYLA çalıştırılır; her adım TASLAK fiş üretir, muhasebeci inceleyip kesinleştirir. */
function YearEndWizard({
  companyId,
  year,
  onClose,
}: {
  companyId: string;
  year: FiscalYear;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [results, setResults] = useState<YearEndResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const stepIndex = results.length;
  const step = YEAR_END_STEPS[stepIndex];

  const run = useMutation({
    mutationFn: async () =>
      (
        await api.POST("/accounting/fiscal-years/{id}/year-end", {
          params: { path: { id: year.id } },
          body: { step: step! },
        })
      ).data!,
    onMutate: () => setError(null),
    onSuccess: async (result) => {
      setResults((current) => [...current, result]);
      await queryClient.invalidateQueries({ queryKey: queryKeys.accounting(companyId) });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("accounting.yearEnd.title", { year: year.year })}
      description={t("accounting.yearEnd.description")}
      className="max-w-2xl"
    >
      <div className="grid gap-4">
        <Alert tone="warning">{t("accounting.yearEnd.reviewWarning")}</Alert>
        <ol className="grid gap-3" aria-label={t("accounting.yearEnd.steps")}>
          {YEAR_END_STEPS.map((item, index) => {
            const result = results[index];
            return (
              <li
                key={item}
                aria-current={index === stepIndex ? "step" : undefined}
                className={cn("rounded-md border p-3", index === stepIndex && "border-primary")}
              >
                <p className="flex items-center gap-2 text-sm font-semibold">
                  <span
                    className="flex size-5 items-center justify-center rounded-full bg-muted text-xs"
                    aria-hidden="true"
                  >
                    {result ? <Check className="size-3.5" /> : index + 1}
                  </span>
                  {t(`accounting.yearEnd.step.${item}.title`)}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t(`accounting.yearEnd.step.${item}.hint`)}
                </p>
                {result ? (
                  <div className="mt-2 grid gap-1 text-sm">
                    {result.draftEntryIds.length === 0 ? (
                      <p>{t("accounting.yearEnd.noDrafts")}</p>
                    ) : (
                      <ul className="flex flex-wrap gap-x-4 gap-y-1">
                        {result.draftEntryIds.map((id, draftIndex) => (
                          <li key={id}>
                            <Link
                              to="/muhasebe/fisler/$id"
                              params={{ id }}
                              className="font-medium text-primary underline underline-offset-4"
                            >
                              {t("accounting.yearEnd.draftLink", { no: draftIndex + 1 })}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                    {result.notes.length > 0 ? (
                      <ul className="grid list-disc gap-0.5 pl-5 text-muted-foreground">
                        {result.notes.map((note) => (
                          <li key={note}>{note}</li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t("common.close")}
          </Button>
          {step ? (
            <Button loading={run.isPending} onClick={() => run.mutate()}>
              {t("accounting.yearEnd.run", { step: stepIndex + 1 })}
            </Button>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}
